/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ApiService from 'cio/services/api';
import CampaignModel, { ParsedTreeBranchNode } from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import DS from 'ember-data';
import isEqual from 'lodash.isequal';
import { edgesEndingInExitCount, getLastBranchExits } from 'cio/utils/visual-workflow-builder/branching';
import { WorkflowCopyModalActions } from 'cio/utils/analytics/transforms/campaign';

interface VisualWorkflowBuilderV2CopyFromModalArgs {
  campaign: CampaignModel;
  confirmSelection: (environmentId: string, campaign: CampaignModel, highlightedNodeIds: string[]) => unknown;
  onClose: () => unknown;
  trackEvent: (modalActionType: WorkflowCopyModalActions, data: { actions: ActionModel[] }) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class VisualWorkflowBuilderV2CopyFromModal extends Component<VisualWorkflowBuilderV2CopyFromModalArgs> {
  @service api!: ApiService;
  @service store!: DS.Store;

  @tracked selectedEnvironmentId = '';
  @tracked selectedCampaignId = '';

  @tracked allHighlightableNodeIds: string[] = [];

  get isSelectEntireWorkflowCheckboxChecked() {
    return (
      !!this.selectedEnvironmentId &&
      !!this.selectedCampaignId &&
      isEqual([...this.allHighlightableNodeIds].sort(), [...this.highlightedNodeIds].sort())
    );
  }

  @tracked highlightedNodeIds: string[] = [];
  @tracked canPerformCopy = false;

  @action
  setHighlightableNodes(campaignModel: CampaignModel, actionModels: ActionModel[]) {
    if (!campaignModel) {
      return;
    }

    campaignModel.parseTree();

    let { parentConsolidatedBranchMap, consolidatedBranchDefaultActions } = campaignModel;

    let allNonEntryAndNonVariationActionIds = (actionModels ?? [])
      .filter((actionModel) => {
        if (actionModel.type === 'multi_lang_branch_action' || actionModel.type === 'filter_match_delay_action') {
          return false;
        }

        if (parentConsolidatedBranchMap[actionModel.id] && !consolidatedBranchDefaultActions.has(actionModel.id)) {
          // we only want to copy the default action if it's part of a multi-language branch
          return false;
        }

        return !actionModel.isVariation;
      })
      .map((actionModel) => actionModel.id);

    let lastWorkflowTreeNode = campaignModel.branchTree[campaignModel.branchTree.length - 1];

    if (lastWorkflowTreeNode.action.isExit) {
      allNonEntryAndNonVariationActionIds = allNonEntryAndNonVariationActionIds.filter(
        (id) => id !== lastWorkflowTreeNode.id,
      );
    } else if (lastWorkflowTreeNode.type === 'branch') {
      if (edgesEndingInExitCount(lastWorkflowTreeNode) === lastWorkflowTreeNode.edges.length) {
        let exitIds: string[] = getLastBranchExits(lastWorkflowTreeNode).map((n: ParsedTreeBranchNode) => n.action.id);

        allNonEntryAndNonVariationActionIds = allNonEntryAndNonVariationActionIds.filter((id) => !exitIds.includes(id));
      }
    }

    this.allHighlightableNodeIds = allNonEntryAndNonVariationActionIds;
  }

  @action
  copySelected() {
    if (this.highlightedNodeIds.length === 0) {
      return;
    }

    let fakeCampaignModel = this.store.peekRecord('campaign', `copy_${this.selectedCampaignId}`);
    if (!fakeCampaignModel) {
      return;
    }

    this.args.confirmSelection(this.selectedEnvironmentId, fakeCampaignModel, this.highlightedNodeIds);
  }

  @action
  onSelectEnvironment(environmentId: string) {
    this.selectedEnvironmentId = environmentId;
    this.highlightedNodeIds = [];
  }

  @action
  onSelectCampaign(campaignId: string) {
    this.selectedCampaignId = campaignId;
    this.highlightedNodeIds = [];
  }

  @action
  onUpdateHighlightedNodeIds(highlightedNodeIds: string[]) {
    this.highlightedNodeIds = highlightedNodeIds;
  }

  @action
  highlightEntireWorkflow() {
    if (this.isSelectEntireWorkflowCheckboxChecked) {
      this.highlightedNodeIds = [];
    } else {
      this.highlightedNodeIds = [...this.allHighlightableNodeIds];
      this.canPerformCopy = true;
    }
  }

  @action
  onCanPerformHighlightOperationChanged({ canCopy }: { canCopy: boolean }) {
    this.canPerformCopy = canCopy;
  }

  @action
  trackEvent(actionType: WorkflowCopyModalActions) {
    this.args.trackEvent(actionType, {
      actions: this.highlightedNodeIds.map((id) => this.store.peekRecord('action', id)) as ActionModel[],
    });
  }
}
